import { ActivatedRouteSnapshot, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { iif, Observable, switchMap, of, map, tap } from 'rxjs';
import { AppRoute } from 'src/app/app-routing.module';
import { UserPermission } from 'src/app/core/enums/user/user-permission.enum';
import { UserRole } from 'src/app/core/enums/user/user-role.enum';
import { UserProfile } from 'src/app/core/models/user/user-profile.model';
import { AuthenticationActions } from '../../authentication/action-types';
import { isUserLoggedIn, userProfile } from '../../user/user.selectors';
import { isAuthenticated } from 'src/app/shared/authentication/authentication.selectors'


export abstract class UserBaseGuard  {

  protected isUserAuthenticated$: Observable<boolean>
  protected user$: Observable<UserProfile | undefined>

  constructor(readonly appStore: Store,
    protected readonly router: Router) {
    this.isUserAuthenticated$ = appStore.pipe(isAuthenticated)
    this.user$ = appStore.pipe(userProfile)
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.isUserAuthenticated$.pipe(
      switchMap(isLogged => !isLogged ? this.auth(window.location.href) : of(true)))

  }

  canLoad(
    route: Route,
    segments: UrlSegment[]): Observable<boolean | UrlTree> {

    return this.isUserAuthenticated$.pipe(
      switchMap(isLogged => !isLogged ? this.auth(window.location.href) : of(true)))

  }

  protected auth(redirectToUrl: string): Observable<UrlTree> {

    return of(
      this.appStore.dispatch(AuthenticationActions.signIn(
        {
          landingUrl: redirectToUrl
        }
      ))).pipe(
        map(() => this.unauthorizedPageUrl) // maybe another
      )
  }

  protected get unauthorizedPageUrl(): UrlTree {
    return this.router.parseUrl(AppRoute[AppRoute.unauthorized]);
  }

  protected isAgent(user: UserProfile | undefined | null): boolean | UrlTree {
    if (!user) return false;
    return user.roles?.some(role => role == UserRole.LobbyAgent)
  }

  protected isAgentSupervisor(user: UserProfile | undefined | null): boolean {
    if (!user) return false;
    return user.roles?.some(role => role == UserRole.LobbyAgentSupervisor) &&
      (
        user.permissions.includes(UserPermission.SearchAnyLead) ||
        user.permissions.includes(UserPermission.SearchHierarchyLead)
      )
  }

  protected isAdministrator(user: UserProfile | undefined | null): boolean {
    if (!user) return false;
    return user.roles?.some(role => role == UserRole.LobbyAdministrator)
  }

  protected isLeadManager(user: UserProfile | undefined | null): boolean {
    if (!user) return false
    return user.roles?.some(role => role == UserRole.LobbyLeadManager)
  }

  protected isAgentManager(userInfo: UserProfile | undefined | null): boolean {
    if (!userInfo) return false;
    return userInfo.roles?.some(role => role == UserRole.LobbyAgentManager)
  }
}
